/**
 * Small badges showing which drop options were used (Include duplicates, Send in
 * batches, AI Optimization, New domains, non-default shortlink format) — rendered
 * on sent drop rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';

interface OptionBadgesProps {
  forceSend?: boolean | null;
  batchAmount?: number | null;
  aiOptimizationEnabled?: boolean | null;
  useOnlyNewDomains?: boolean | null;
  clickIdStructure?: ShortlinkClickIdStructure | null;
  domainScheme?: ShortlinkDomainScheme | null;
}

const BADGE = 'shrink-0 rounded border px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide';
// Shortlink format is a detail, not a risk — neutral tone so it never competes with Dup/AI.
const SHORTLINK_BADGE = `${BADGE} border-border bg-muted text-muted-foreground`;

/** "Dup · Batches · AI · New · Https · Subdomain" — default shortlink options add nothing. */
export function draftOptionSummary(drop: OptionBadgesProps): string {
  return [
    drop.forceSend && 'Dup',
    drop.batchAmount && 'Batches',
    drop.aiOptimizationEnabled && 'AI',
    drop.useOnlyNewDomains && 'New',
    drop.domainScheme === 'https' && 'Https',
    drop.clickIdStructure === 'subdomain' && 'Subdomain',
  ].filter(Boolean).join(' · ');
}

export const OptionBadges: FC<OptionBadgesProps> = ({
  forceSend,
  batchAmount,
  aiOptimizationEnabled,
  useOnlyNewDomains,
  clickIdStructure,
  domainScheme,
}) => (
  <>
    {forceSend ? (
      <span
        className={`${BADGE} border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100`}
        title="Include duplicates — recipients may already have received an SMS today"
      >
        Dup
      </span>
    ) : null}
    {batchAmount ? (
      <span
        className={`${BADGE} border-sky-500/40 bg-sky-500/10 text-sky-900 dark:text-sky-100`}
        title="Sent in batches"
      >
        Batches
      </span>
    ) : null}
    {aiOptimizationEnabled ? (
      <span
        className={`${BADGE} border-violet-500/40 bg-violet-500/10 text-violet-900 dark:text-violet-100`}
        title="AI Optimization enabled"
      >
        AI
      </span>
    ) : null}
    {useOnlyNewDomains ? (
      <span
        className={`${BADGE} border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100`}
        title="Use only new (unused) domains"
      >
        New
      </span>
    ) : null}
    {domainScheme === 'https' ? (
      <span className={SHORTLINK_BADGE} title="Shortlinks start with https://">
        Https
      </span>
    ) : null}
    {clickIdStructure === 'subdomain' ? (
      <span className={SHORTLINK_BADGE} title="Click id in the subdomain (abc123.domain.com)">
        Subdomain
      </span>
    ) : null}
  </>
);
